import {
  ActionReducerMapBuilder,
  EntityState,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';
import { FORM_ERROR } from 'final-form';

import { AppError } from '@vk-hr-tek/core/error';
import { History } from '@vk-hr-tek/core/history';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';

import { ThunkExtra, ThunkValuesAndActions } from '@app/store';
import { DynamicEntity } from '@app/types';
import {
  DashboardService,
  GetUserWidgetSurveysParams,
  GetUserWidgetSurveysResponse,
  GetWidgetSurveysResponse,
} from '@app/gen/dashboard';

import { DashboardState } from '../../dashboard.state';
import { SurveysWidgetSettingsDto } from '../../../dto';

export const getSurveysList = createAsyncThunk<
  GetWidgetSurveysResponse['surveys'],
  void,
  ThunkExtra
>(
  'dashboard/getSurveysList',
  async (_, { rejectWithValue, extra: { inject } }) => {
    try {
      const result = await inject(DashboardService).getWidgetSurveys();

      return result.surveys;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getSurveysSettings = createAsyncThunk<
  GetUserWidgetSurveysResponse,
  GetUserWidgetSurveysParams,
  ThunkExtra
>(
  'dashboard/getSurveysSettings',
  async (params, { rejectWithValue, extra: { inject } }) => {
    try {
      const result = await inject(DashboardService).getUserWidgetSurveys({
        params,
      });

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateSurveyCustomName = createAsyncThunk<
  void,
  ThunkValuesAndActions<SurveysWidgetSettingsDto>,
  ThunkExtra
>(
  'dashboard/updateSurveyCustomName',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const history = inject<History>(History);
      const service = inject(DashboardService);

      await service.updateUserWidgetSurveys({
        body: {
          custom_name: values.name,
        },
      });

      actions.resolve(null);
      history.push('/');
      dispatch(
        showCustomNotification([
          {
            title: 'Виджет успешно сохранён!',
            message: 'Опросы',
            type: 'info',
          },
        ]),
      );
    } catch (err) {
      const history = inject<History>(History);

      actions.reject({ [FORM_ERROR]: err });
      history.push('/');
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить виджет',
            message: 'Опросы',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const surveysReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<DynamicEntity, string> & DashboardState
  >,
) => {
  builder.addCase(getSurveysList.pending, (state) => {
    state.surveys.status = 'loading';
  });
  builder.addCase(getSurveysList.fulfilled, (state, { payload }) => {
    state.surveys.status = 'complete';
    state.surveys.items = payload;
  });
  builder.addCase(getSurveysList.rejected, (state) => {
    state.surveys.status = 'failed';
  });
  builder.addCase(getSurveysSettings.pending, (state) => {
    state.surveysSettings.status = 'loading';
    state.surveysSettings.error = null;
  });
  builder.addCase(getSurveysSettings.fulfilled, (state, { payload }) => {
    state.surveysSettings.status = 'complete';
    state.surveysSettings.name = payload.custom_name;
    state.surveysSettings.error = null;
  });
  builder.addCase(getSurveysSettings.rejected, (state, { payload, error }) => {
    state.surveysSettings.status = 'failed';
    state.surveysSettings.error =
      payload ||
      ({
        info: (error && error.message) || 'Unknown error',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
};
